import {inject, Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {environment} from "../../environments/environment";
import {catchError, first} from "rxjs/operators";
import {Observable, throwError} from "rxjs";
import {RemoteResponse} from "../shared/remote-response";
import { CustomEncoder } from "../shared/classes/custom-encoder";
import { ErrorMessageService } from "./error-message.service";

@Injectable({
  providedIn: 'root'
})
export class ApiCallService {
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly http = inject(HttpClient);
  private readonly debugMode = environment.enableDebug;

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8'
    }),
    withCredentials: true,
    params: {}
  }
  getData(url:string, params): Observable<RemoteResponse> {
    this.httpOptions.params = params;
    return this.http.get<RemoteResponse>(environment.apiUrl + environment.baseUrl + url, this.httpOptions)
      .pipe(
        catchError(this.errorHandle)
      );
  }

  getStaticJsonData(jsonFile:string): Observable<any> {
    return this.http.get<RemoteResponse>(environment.s3Bucket + "/assets/json/" + jsonFile, this.httpOptions)
      .pipe(
        catchError(this.errorHandle), first(),
      );
  }

  //I did this just for backwards compatibility
  getDataAsPromise(url:string, params): Promise<RemoteResponse> {
    return this.getData(url, params).toPromise();
  }

  postData(url:string, data): Observable<RemoteResponse> {
    this.httpOptions.params = {};
    const body = new HttpParams({fromObject: data, encoder: new CustomEncoder()});
    return this.http.post<RemoteResponse>(environment.apiUrl + environment.baseUrl + url, body.toString(), this.httpOptions)
      .pipe(
        catchError(this.errorHandle)
      );
  }

  //I did this just for backwards compatibility
  postDataAsPromise(url:string, data): Promise<RemoteResponse> {
    return this.postData(url, data).toPromise();
  }

  upload(url:string, file: File, fileVariableName: string, data?, filename?: string): Observable<any>{
    const uploadUrl = environment.apiUrl + environment.baseUrl + url;
    let formData = new FormData();
    if(filename){
      formData.append(fileVariableName, file, filename);
    }
    else{
      formData.append(fileVariableName, file);
    }
    if(typeof data !== "undefined"){
      for(let key of Object.keys(data)){
        formData.append(key, data[key]);
      }
    }
    return this.http.post<any>(uploadUrl, formData, {
      withCredentials: true,
      reportProgress: true,
      observe: 'events'
    });
  }

  postJsonData(url:string, data): Observable<RemoteResponse> {
    this.httpOptions.params = {};
    const body = new HttpParams({fromObject: {jsondata: JSON.stringify(data)}, encoder: new CustomEncoder()});
    return this.http.post<RemoteResponse>(environment.apiUrl + environment.baseUrl + url, body.toString(), this.httpOptions)
      .pipe(
        catchError(this.errorHandle)
      );
  }

  postJsonDataAndFiles(url:string, data: any, files: File[]): Observable<RemoteResponse> {
    // 1. Create a FormData object
    const formData = new FormData();

    // 2. Append the JSON data (as a string)
    // The backend will need to parse this string back into JSON
    formData.append('jsondata', JSON.stringify(data));

    // 3. Append each file
    // The key 'files' will be used by the backend to retrieve the file array
    if (files && files.length > 0) {
      let fileIndex = 0;
      for (const file of files) {
        formData.append('doc' + fileIndex++, file, file.name);
      }
    }

    // 4. Make the POST request with the FormData object
    // DO NOT set the 'Content-Type' header manually. The browser will do it.
    return this.http.post<RemoteResponse>(
      environment.apiUrl + environment.baseUrl + url,
      formData, // Use formData as the body
      {
        withCredentials: true,
        reportProgress: false,
        //observe: 'events'
      }
    )
    .pipe(
      catchError(error => this.errorHandle(error))
    );
  }

  postJsonDataAndRepeaterFiles(url:string, data: any, files: {file:File, delete:boolean}[]): Observable<RemoteResponse> {
    // 1. Create a FormData object
    const formData = new FormData();

    // 2. Append the JSON data (as a string)
    // The backend will need to parse this string back into JSON
    formData.append('jsondata', JSON.stringify(data));

    // 3. Append each file
    // The key 'files' will be used by the backend to retrieve the file array
    if (files && files.length > 0) {
      let fileIndex = 0;
      for (const file of files) {
        if(file.file) {
          formData.append( 'doc' + fileIndex, file.file, file.file.name );
        }
        formData.append( 'docDelete' + fileIndex, file.delete + "" );
        fileIndex++;
      }
    }

    // 4. Make the POST request with the FormData object
    // DO NOT set the 'Content-Type' header manually. The browser will do it.
    return this.http.post<RemoteResponse>(
      environment.apiUrl + environment.baseUrl + url,
      formData, // Use formData as the body
      {
        withCredentials: true,
        reportProgress: false,
        //observe: 'events'
      }
    )
      .pipe(
        catchError(error => this.errorHandle(error))
      );
  }

  // Error handling
  errorHandle = (error) => {
    let errorMessage: string;
    if(error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.error(errorMessage);
    if(this.debugMode){
      this.errorMessageService.showErrorModal("Error", [errorMessage]);
    }
    //todo stop loader
    //this.blockUIService.resetGlobal();
    return throwError(errorMessage);
  }

  getRestURL(addonName) {
    return environment.apiUrl + environment.baseUrl + addonName;
  }
}
